import React from 'react';
import Head from 'next/head';

const TermsPage: React.FC = () => {
  return (
    <>
      <Head>
        <title>Privacy Policy- Nestflo</title>
        <meta name="description" content="Website Privacy Policy for Nestflo" />
      </Head>

      {/* Header */}
      <header
        className="shadow-sm"
        style={{ backgroundColor: 'rgb(18, 82, 133)', height: '96px' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-0">
          <div className="flex justify-between items-center py-4"></div>
        </div>
      </header>

      {/* Main Content */}
      <main
        className="!bg-white min-h-screen"
        style={{ color: 'rgb(18, 82, 133)' }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Title Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Website Privacy Policy
            </h1>
            <p className="text-xl text-gray-700 mb-2">
              Kunpro Ltd, trading as Nestflo
            </p>
            <p className="text-lg text-gray-600 mb-2">Website Privacy Policy</p>
            <p className="text-lg text-gray-600">June 2025</p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              1. Who we are
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>1.1.</strong> We are Kunpro Ltd, trading as Nestflo is a
                limited company registered in England and Wales, company number
                13908049 and registered address at Unit 111, Filwood Green
                Business Park, 1 Filwood Park Ln, Bristol, England, BS4 1ET.
              </p>

              <p>
                <strong>1.2.</strong> We provide 24/7 instant responses,
                automated lead generation, and pre-qualification to letting
                agencies.
              </p>

              <p>
                <strong>1.3.</strong> You can contact us by phone: 03301338626
                or by email:{' '}
                <a
                  href="mailto:hello@nestflo.ai"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  hello@nestflo.ai
                </a>
                .
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              2. Scope of this policy
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>2.1.</strong> We respect your privacy and we are
                committed to protecting your personal data. This privacy policy
                will inform you as to how we look after your personal data when
                you visit{' '}
                <a
                  href="https://nestflo.ai"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  https://nestflo.ai
                </a>{' '}
                (&quot;Website&quot;), (regardless of where you visit it from)
                use our messaging and audio chatbots (Chatbots) and when you
                purchase our products and services. This privacy policy will
                also tell you about your privacy rights and how the law protects
                you.
              </p>

              <p>
                <strong>2.2.</strong> It is important that you read this privacy
                policy together with any other privacy policy or fair processing
                policy we may provide on specific occasions when we are
                collecting or processing personal data about you so that you are
                fully aware of how and why we are using your data. This privacy
                policy supplements other notices and privacy policies and is not
                intended to override them.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              3. Controller
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>3.1.</strong> We are the controller and responsible for
                your personal data.
              </p>

              <p>
                <strong>3.2.</strong> We have appointed a data privacy manager
                who is responsible for overseeing questions in relation to this
                privacy policy. If you have any questions about this privacy
                policy, including any requests to exercise your legal rights,
                please contact the data privacy manager using the contact
                details set out above.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              4. Contacting the Information Commissioner&apos;s Office
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>4.1.</strong> You have the right to make a complaint at
                any time to the Information Commissioner&apos;s Office (ICO),
                the UK regulator for data protection issues (
                <a
                  href="https://www.ico.org.uk"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  www.ico.org.uk
                </a>
                ).
              </p>

              <p>
                <strong>4.2.</strong> We would, however, appreciate the chance
                to deal with your concerns before you approach the ICO so please
                contact us in the first instance.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              5. Changes to the privacy policy and your duty to inform us of
              changes
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>5.1.</strong> We keep our privacy policy under regular
                review. This version was last updated on 01 June 2025.
              </p>

              <p>
                <strong>5.2.</strong> It is important that the personal data we
                hold about you is accurate and current. Please keep us informed
                if your personal data changes during your relationship with us.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              6. Third-party links
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>6.1.</strong> The Website and our Chatbots may include
                links to third-party websites, plug-ins and applications.
                Clicking on those links or enabling those connections may allow
                third parties to collect or share data about you.
              </p>

              <p>
                <strong>6.2.</strong> We do not control these third-party
                websites and are not responsible for their privacy statements.
                When you leave our Website, we encourage you to read the privacy
                policy of every website you visit.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              7. The data we collect about you
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>7.1.</strong> Personal data, or personal information,
                means any information about an individual from which that person
                can be identified. It does not include data where the identity
                has been removed (anonymous data).
              </p>

              <p>
                <strong>7.2.</strong> We may collect, use, store and transfer
                different kinds of personal data about you which we have grouped
                together as follows:
              </p>

              <div className="ml-6 space-y-4">
                <p>
                  <strong>Identity Data:</strong> includes first name, maiden
                  name, last name, username or similar identifier, marital
                  status, date of birth and gender.
                </p>

                <p>
                  <strong>Contact Data:</strong> includes billing address,
                  delivery address, email address and telephone numbers.
                </p>

                <p>
                  <strong>Technical Data:</strong> includes internet protocol
                  (IP) address, your login data, browser type and version, time
                  zone setting and location, browser plug-in types and versions,
                  operating system and platform, and other technology on the
                  devices you use to access the Website, our Chatbots and our
                  other products and services.
                </p>

                <p>
                  <strong>Profile Data:</strong> includes your username and
                  password, purchases or orders made by you, your interests,
                  preferences, feedback and survey responses.
                </p>

                <p>
                  <strong>Usage Data:</strong> includes information about how
                  you use the Website, our Chatbots and our other products and
                  services.
                </p>

                <p>
                  <strong>Marketing and Communications Data:</strong> includes
                  your preferences in receiving marketing from us and our third
                  parties and your communication preferences.
                </p>
              </div>

              <p>
                <strong>7.3.</strong> We also collect, use and share{' '}
                <strong>Aggregated Data</strong> such as statistical or
                demographic data for any purpose. Aggregated Data could be
                derived from your personal data but is not considered personal
                data in law as this data will not directly or indirectly reveal
                your identity. However, if we combine or connect Aggregated Data
                with your personal data so that it can directly or indirectly
                identify you, we treat the combined data as personal data which
                will be used in accordance with this privacy policy.
              </p>

              <p>
                <strong>7.4.</strong> Except under clause 7.4 above, we do{' '}
                <strong>NOT</strong> collect any{' '}
                <strong>Special Categories of Personal Data</strong> about you
                (this includes details about your race or ethnicity, religious
                or philosophical beliefs, sexlife, sexual orientation, political
                opinions, trade union membership, genetic and biometric data).
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              8. If you fail to provide personal data
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>8.1.</strong> Where we need to collect personal data by
                law, or under the terms of a contract we have with you (for
                example, a contract to provide you our Chatbots and our other
                products and services), and you fail to provide that data when
                requested, we may not be able to perform the contract we have or
                are trying to enter into with you. In this case, we may have to
                cancel a product or service you have with us, but we will notify
                you if this is the case at the time.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              9. How is your personal data collected?
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>9.1.</strong> We use different methods to collect data
                from and about you including through:
              </p>

              <div className="ml-6 space-y-4">
                <div>
                  <p className="font-semibold">Direct interactions.</p>
                  <p>
                    You may give us your personal data by filling in forms,
                    using our Chatbots or by corresponding with us by phone,
                    email or otherwise. This includes personal data you provide
                    when you:
                  </p>
                  <ul className="ml-6 mt-2 space-y-1">
                    <li>• apply for our products or services;</li>
                    <li>• use our Chatbots;</li>
                    <li>• create an account on the Website;</li>
                    <li>• authorise marketing to be sent to you;</li>
                    <li>• enter a competition, promotion or survey; or</li>
                    <li>• give us feedback or contact us.</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold">
                    Automated technologies or interactions.
                  </p>
                  <p>
                    As you interact with our Website and our chatbot, we will
                    automatically collect Technical Data about your equipment,
                    browsing actions and patterns. We collect this personal data
                    by using cookies, server logs and other similar
                    technologies. We may also receive Technical Data about you
                    if you visit other websites employing our cookies.
                  </p>
                </div>

                <div>
                  <p className="font-semibold">
                    Third parties or publicly available sources.
                  </p>
                  <p>
                    We will receive personal data about you from various third
                    parties and public sources (sometimes based outside the UK),
                    like:
                  </p>
                  <ul className="ml-6 mt-2 space-y-1">
                    <li>• analytics providers such as Google;</li>
                    <li>• advertising networks;</li>
                    <li>• search information providers;</li>
                    <li>
                      • providers of technical, payment and delivery services;
                    </li>
                    <li>• data brokers or aggregators; and</li>
                    <li>• publicly available sources.</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              10. How we use your personal data
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>10.1.</strong> We will only use your personal data when
                the law allows us to. Most commonly, we will use your personal
                data in the following circumstances:
              </p>

              <div className="ml-6 space-y-4">
                <p>
                  Where we need to perform the contract we are about to enter
                  into or have entered into with you.
                </p>

                <p>
                  Where it is necessary for our legitimate interests (or those
                  of a third party) and your interests and fundamental rights do
                  not override those interests.
                </p>

                <p>Where we need to comply with a legal obligation.</p>
              </div>

              <p>
                <strong>10.2.</strong> Generally, we do not rely on consent as a
                legal basis for processing your personal data although we will
                get your consent before sending third party direct marketing
                communications to you via email or text message. You have the
                right to withdraw consent to marketing at any time by contacting
                us.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              11. Purposes for which we will use your personal data
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>11.1.</strong> We have set out below, a description of
                the ways we plan to use your personal data, and which of the
                legal bases we rely on to do so. We have also identified what
                our legitimate interests are where appropriate.
              </p>

              <p>
                <strong>11.2.</strong> Note that we may process your personal
                data for more than one lawful ground depending on the specific
                purpose for which we are using your data.
              </p>

              <p>
                <strong>11.3.</strong> Please contact us if you need details
                about the specific legal ground we are relying on to process
                your personal data where more than one ground has been set out
                in the table below.
              </p>

              <div className="overflow-x-auto mt-8">
                <table
                  className="min-w-full border border-gray-300"
                  style={{ tableLayout: 'fixed' }}
                >
                  <thead>
                    <tr className="bg-gray-50">
                      <th
                        className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900"
                        style={{ width: '35%' }}
                      >
                        Purpose/Activity
                      </th>
                      <th
                        className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900"
                        style={{ width: '20%' }}
                      >
                        Type of data
                      </th>
                      <th
                        className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900"
                        style={{ width: '45%' }}
                      >
                        Lawful basis for processing (including basis of
                        legitimate interest)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        To register you as a new customer
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        (a) Identity <br />
                        (b) Contact
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        Performance of a contract with you
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        To process and deliver your order including: <br />
                        (a) Manage payments, fees and charges <br />
                        (b) Collect and recover money owed to us
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        (a) Identity <br />
                        (b) Contact <br />
                        (e) Marketing and Communications
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        (a) Performance of a contract with you <br />
                        (b) Necessary for our legitimate interests (to recover
                        debts due to us)
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        To manage our relationship with you which will include:{' '}
                        <br />
                        (a) Notifying you about changes to our terms or privacy
                        policy <br />
                        (b) Asking you to leave a review or take a survey
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        (a) Identity <br />
                        (b) Contact <br />
                        (c) Profile <br />
                        (d) Marketing and Communications
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        (a) Performance of a contract with you <br />
                        (b) Necessary to comply with a legal obligation <br />
                        (c) Necessary for our legitimate interests (to keep our
                        records updated and to study how customers use our
                        products/services)
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        To enable you to partake in a prize draw, competition or
                        complete a survey
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        (a) Identity <br />
                        (b) Contact <br />
                        (c) Profile <br />
                        (d) Usage <br />
                        (e) Marketing and Communications
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        (a) Performance of a contract with you <br />
                        (b) Necessary for our legitimate interests (to study how
                        customers use our products/services, to develop them and
                        grow our business)
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        To administer and protect our business and this website
                        (including troubleshooting, data analysis, testing,
                        system maintenance, support, reporting and hosting of
                        data)
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        (a) Identity <br />
                        (b) Contact <br />
                        (c) Technical
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        (a) Necessary for our legitimate interests (for running
                        our business, provision of administration and IT
                        services, network security, to prevent fraud and in the
                        context of a business reorganisation or group
                        restructuring exercise) <br />
                        (b) Necessary to comply with a legal obligation
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        To deliver relevant website content and advertisements
                        to you and measure or understand the effectiveness of
                        the advertising we serve to you
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        (a) Identity <br />
                        (b) Contact <br />
                        (c) Profile <br />
                        (d) Usage <br />
                        (e) Marketing and Communications <br />
                        (f) Technical
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        Necessary for our legitimate interests (to study how
                        customers use our products/services, to develop them, to
                        grow our business and to inform our marketing strategy)
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        To use data analytics to improve our website,
                        products/services, marketing, customer relationships and
                        experiences
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        (a) Technical <br />
                        (b) Usage
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        Necessary for our legitimate interests (to define types
                        of customers for our products and services, to keep our
                        website updated and relevant, to develop our business
                        and to inform our marketing strategy)
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        To make suggestions and recommendations to you about
                        goods or services that may be of interest to you
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        (a) Identity <br />
                        (b) Contact <br />
                        (c) Technical <br />
                        (d) Usage <br />
                        (e) Profile <br />
                        (f) Marketing and Communications
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-gray-700 whitespace-pre-line">
                        Necessary for our legitimate interests (to develop our
                        products/services and grow our business)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              12. Marketing
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>12.1.</strong> We strive to provide you with choices
                regarding certain personal data uses, particularly around
                marketing and advertising.
              </p>

              <p>
                <strong>12.2.</strong> We may use your Identity, Contact,
                Technical, Usage and Profile Data to form a view on what we
                think you may want or need, or what may be of interest to you.
                This is how we decide which products, services and offers may be
                relevant for you (we call this marketing).
              </p>

              <p>
                <strong>12.3.</strong> You will receive marketing communications
                from us if you have requested information from us or agreed to
                receive such marketing communications.
              </p>

              <p>
                <strong>12.4.</strong> We will get your express opt-in consent
                before we share your personal data with any third party for
                marketing purposes.
              </p>

              <p>
                <strong>12.5.</strong> You can ask us or third parties to stop
                sending you marketing messages at any time by following the
                opt-out links on any marketing message sent to you or by
                contacting us at any time.
              </p>

              <p>
                <strong>12.6.</strong> Where you opt out of receiving these
                marketing messages, this will not apply to personal data
                provided to us as a result of a product/service purchase,
                warranty registration, product/service experience or other
                transactions.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              13. Cookies
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>13.1.</strong> You can set your browser to refuse all or
                some browser cookies, or to alert you when websites set or
                access cookies. If you disable or refuse cookies, please note
                that some parts of the Website or our Chatbots may become
                inaccessible or not function properly.
              </p>

              <p>
                <strong>13.2.</strong> For more information about the cookies we
                use, please see{' '}
                <a
                  href="https://nestflo.ai/assets/documents/cookie-policy.pdf"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  https://nestflo.ai/assets/documents/cookie-policy.pdf
                </a>
                .
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              14. Change of purpose
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>14.1.</strong> We will only use your personal data for
                the purposes for which we collected it, unless we reasonably
                consider that we need to use it for another reason and that
                reason is compatible with the original purpose. If you wish to
                get an explanation as to how the processing for the new purpose
                is compatible with the original purpose, please contact us.
              </p>

              <p>
                <strong>14.2.</strong> If we need to use your personal data for
                an unrelated purpose, we will notify you and we will explain the
                legal basis which allows us to do so.
              </p>

              <p>
                <strong>14.3.</strong> Please note that we may process your
                personal data without your knowledge or consent, in compliance
                with the above rules, where this is required or permitted by
                law.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              15. Disclosures of your personal data
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>15.1.</strong> We may share your personal data with the
                following third parties for the purposes set out in this privacy
                policy.
              </p>

              <div className="ml-6 space-y-4">
                <p>
                  Service providers who provide IT and system administration
                  services, OpenAI,{' '}
                  <a
                    href="https://openai.com/"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    https://openai.com/
                  </a>{' '}
                  and deepseek,{' '}
                  <a
                    href="https://www.deepseek.com/en"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    https://www.deepseek.com/en
                  </a>
                  .
                </p>

                <p>
                  We share anonymous data with OpenAI and deepseek, such as
                  conversation details (excluding usernames, contact details,
                  etc.) between users and the Nestflo automated chatbot.
                </p>

                <p>HM Revenue & Customs, regulators and other authorities.</p>

                <p>
                  Third parties to whom we may choose to sell, transfer or merge
                  parts of our business or our assets. Alternatively, we may
                  seek to acquire other businesses or merge with them. If a
                  change happens to our business, then the new owners may use
                  your personal data in the same way as set out in this privacy
                  policy.
                </p>
              </div>

              <p>
                <strong>15.2.</strong> We require all third parties to respect
                the security of your personal data and to treat it in accordance
                with the law. We do not allow our third-party service providers
                to use your personal data for their own purposes and only permit
                them to process your personal data for specified purposes and in
                accordance with our instructions.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              16. International transfers
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>16.1.</strong> Some of our external third parties are
                based outside the UK and the EEA so their processing of your
                personal data will involve a transfer of data outside the UK and
                the EEA.
              </p>

              <p>
                <strong>16.2.</strong> Whenever we transfer your personal data
                out of the UK and the EEA, we ensure a similar degree of
                protection is afforded to it by ensuring at least one of the
                following safeguards is implemented:
              </p>

              <div className="ml-6 space-y-4">
                <p>
                  We will only transfer your personal data to countries that
                  have been deemed to provide an adequate level of protection
                  for personal data.
                </p>

                <p>
                  Where we use certain service providers, we may use specific
                  contracts approved for use in the UK which give personal data
                  the same protection it has in the UK.
                </p>
              </div>

              <p>
                <strong>16.3.</strong> Please contact us if you want further
                information on the specific mechanism used by us when
                transferring your personal data out of the UK and the EEA.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              17. Data security
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>17.1.</strong> We have put in place appropriate security
                measures to prevent your personal data from being accidentally
                lost, used or accessed in an unauthorised way, altered or
                disclosed. In addition, we limit access to your personal data to
                those employees, agents, contractors and other third parties who
                have a business need to know. They will only process your
                personal data on our instructions and they are subject to a duty
                of confidentiality.
              </p>

              <p>
                <strong>17.2.</strong> We have put in place procedures to deal
                with any suspected personal data breach and will notify you and
                any applicable regulator of a breach where we are legally
                required to do so.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              18. Data retention
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>18.1.</strong> We will only retain your personal data
                for as long as reasonably necessary to fulfil the purposes we
                collected it for, including for the purposes of satisfying any
                legal, regulatory, tax, accounting or reporting requirements. We
                may retain your personal data for a longer period in the event
                of a complaint or if we reasonably believe there is a prospect
                of litigation in respect to our relationship with you.
              </p>

              <p>
                <strong>18.2.</strong> To determine the appropriate retention
                period for personal data, we consider the amount, nature and
                sensitivity of the personal data, the potential risk of harm
                from unauthorised use or disclosure of your personal data, the
                purposes for which we process your personal data and whether we
                can achieve those purposes through other means, and the
                applicable legal, regulatory, tax, accounting or other
                requirements.
              </p>

              <p>
                <strong>18.3.</strong> In some circumstances we will anonymise
                your personal data (so that it can no longer be associated with
                you) for research or statistical purposes, in which case we may
                use this information indefinitely without further notice to you.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              19. Your legal rights
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>19.1.</strong> Under certain circumstances, you have
                rights under data protection laws in relation to your personal
                data.
              </p>

              <p>
                <strong>19.2.</strong> You have right to{' '}
                <strong>request access</strong> to your personal data (commonly
                known as a &quot;data subject access request&quot;). This
                enables you to receive a copy of the personal data we hold about
                you and to check that we are lawfully processing it.
              </p>

              <p>
                <strong>19.3.</strong> You have right to{' '}
                <strong>request correction</strong> of the personal data that we
                hold about you. This enables you to have any incomplete or
                inaccurate data we hold about you corrected, though we may need
                to verify the accuracy of the new data you provide to us.
              </p>

              <p>
                <strong>19.4.</strong> You have the right to{' '}
                <strong>request erasure</strong> of your personal data. This
                enables you to ask us to delete or remove personal data where
                there is no good reason for us continuing to process it. You
                also have the right to ask us to delete or remove your personal
                data where you have successfully exercised your right to object
                to processing, where we may have processed your information
                unlawfully or where we are required to erase your personal data
                to comply with local law. Note, however, that we may not always
                be able to comply with your request of erasure for specific
                legal reasons which will be notified to you, if applicable, at
                the time of your request.
              </p>

              <p>
                <strong>19.5.</strong> You have the right to{' '}
                <strong>object to processing</strong> of your personal data
                where we are relying on a legitimate interest (or those of a
                third party) and there is something about your particular
                situation which makes you want to object to processing on this
                ground as you feel it impacts on your fundamental rights and
                freedoms. You also have the right to object where we are
                processing your personal data for direct marketing purposes. In
                some cases, we may demonstrate that we have compelling
                legitimate grounds to process your information which override
                your rights and freedoms.
              </p>

              <p>
                <strong>19.6.</strong> You have the right to{' '}
                <strong>request restriction of processing</strong> of your
                personal data. This enables you to ask us to suspend the
                processing of your personal data in the following scenarios:
              </p>

              <div className="ml-6 space-y-2">
                <p>If you want us to establish the data&apos;s accuracy.</p>
                <p>
                  Where our use of the data is unlawful but you do not want us
                  to erase it.
                </p>
                <p>
                  Where you need us to hold the data even if we no longer
                  require it as you need it to establish, exercise or defend
                  legal claims.
                </p>
                <p>
                  You have objected to our use of your data but we need to
                  verify whether we have overriding legitimate grounds to use
                  it.
                </p>
              </div>

              <p>
                <strong>19.7.</strong> You have the right to{' '}
                <strong>request the transfer</strong> of your personal data to
                you or to a third party. We will provide to you, or a third
                party you have chosen, your personal data in a structured,
                commonly used, machine-readable format. Note that this right
                only applies to automated information which you initially
                provided consent for us to use or where we used the information
                to perform a contract with you.
              </p>

              <p>
                <strong>19.8.</strong> You have the right to{' '}
                <strong>withdraw consent at any time</strong> where we are
                relying on consent to process your personal data. However, this
                will not affect the lawfulness of any processing carried out
                before you withdraw your consent. If you withdraw your consent,
                we may not be able to provide certain products or services to
                you. We will advise you if this is the case at the time you
                withdraw your consent.
              </p>

              <p>
                <strong>19.9.</strong> If you wish to exercise any of the rights
                set out above, please contact us.
              </p>

              <p>
                <strong>19.10.</strong> No fee usually required: You will not
                have to pay a fee to access your personal data (or to exercise
                any of the other rights). However, we may charge a reasonable
                fee if your request is clearly unfounded, repetitive or
                excessive. Alternatively, we could refuse to comply with your
                request in these circumstances.
              </p>

              <p>
                <strong>19.11.</strong> What we may need from you: We may need
                to request specific information from you to help us confirm your
                identity and ensure your right to access your personal data (or
                to exercise any of your other rights). This is a security
                measure to ensure that personal data is not disclosed to any
                person who has no right to receive it. We may also contact you
                to ask you for further information in relation to your request
                to speed up our response.
              </p>

              <p>
                <strong>19.12.</strong> Time limit to respond: We try to respond
                to all legitimate requests within one month. Occasionally it
                could take us longer than a month if your request is
                particularly complex or you have made a number of requests. In
                this case, we will notify you and keep you updated.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              20. About Google Calendar Data
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>20.1. Information We Collect</strong>
              </p>
              <p>
                When you use <strong>Nestflo-Calendar</strong> to schedule
                appointments with real estate agents, we collect and process the
                following data:
              </p>

              <div className="ml-6 space-y-4">
                <p>
                  <strong>Google Calendar Data:</strong> We access your Google
                  Calendar solely to create, view, or update appointment events
                  (e.g., property viewing schedules).
                </p>

                <p>
                  <strong>User-Provided Information:</strong> Such as your name,
                  email address, and any notes you submit when booking an
                  appointment.
                </p>

                <p>
                  <strong>Technical Data:</strong> Including IP address and
                  browser type (for basic analytics and troubleshooting).
                </p>
              </div>

              <p>
                <strong>20.2. How We Use Your Data</strong>
              </p>
              <p>Your data is used exclusively for:</p>

              <div className="ml-6 space-y-2">
                <p>
                  Synchronizing selected appointment times between tenants and
                  agents via Google Calendar.
                </p>
                <p>Sending you confirmation notifications (if applicable).</p>
                <p>
                  We do not use your data for advertising or unrelated purposes.
                </p>
              </div>

              <p>
                <strong>20.3. Data Storage &amp; Security</strong>
              </p>

              <div className="ml-6 space-y-2">
                <p>
                  Calendar data is synced directly through Google Calendar API;
                  we do not store it on our servers unless temporarily required
                  for functionality (encrypted and deleted within 15 days).
                </p>
                <p>
                  Non-calendar data (e.g., email) is retained only as long as
                  necessary to fulfill the booking process.
                </p>
                <p>
                  We use HTTPS encryption, OAuth 2.0, and restrict access to
                  authorized personnel.
                </p>
              </div>

              <p>
                <strong>20.4. Third-Party Sharing</strong>
              </p>
              <p>
                Your data is never sold or shared with third parties except:
              </p>

              <div className="ml-6 space-y-2">
                <p>
                  <strong>Google API Services:</strong> Required to sync
                  calendar events (subject to Google&apos;s Privacy Policy).
                </p>
                <p>Legal compliance (e.g., if required by law).</p>
              </div>

              <p>
                <strong>20.5. Google API Compliance</strong>
              </p>

              <div className="ml-6 space-y-2">
                <p>
                  Our app adheres to{' '}
                  <strong>Google API Services User Data Policy</strong>.
                </p>
                <p>
                  We only request minimum permissions (Google Calendar
                  read/write access) and cannot view data outside the scope of
                  scheduling appointments.
                </p>
              </div>

              <p>
                <strong>20.6. Your Rights</strong>
              </p>

              <div className="ml-6 space-y-2">
                <p>
                  <strong>Revoke Access:</strong> Disable our app&apos;s
                  calendar permissions anytime via your Google Account Settings.
                </p>
                <p>
                  <strong>Delete Data:</strong> Contact us at{' '}
                  <a
                    href="mailto:hello@nestflo.ai"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    hello@nestflo.ai
                  </a>{' '}
                  to request deletion of non-calendar data.
                </p>
              </div>

              <p>
                <strong>20.7. Changes to This Policy</strong>
              </p>
              <p>
                Updates will be posted on this page with a revised date.
                Continued use of the service constitutes acceptance.
              </p>

              <p>
                <strong>20.8. Contact Us</strong>
              </p>
              <p>
                For privacy-related questions, email{' '}
                <a
                  href="mailto:hello@nestflo.ai"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  hello@nestflo.ai
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default TermsPage;
